import { useState, useMemo, useEffect, useRef } from 'react';
import { useScore } from '../../../context/ScoreContext';
import { useRatings, Rating } from '../../../hooks/useRatings';
import { useRatingArchives, RatingArchive } from '../../../hooks/useRatingArchives';
import { useRatingItems, RatingItem } from '../../../hooks/useRatingItems';
import { useRatingResultBroadcast, RatingResultEntry } from '../../../hooks/useRatingResultBroadcast';

const FONT_BODY = "'Rajdhani', 'Noto Sans TC', system-ui, sans-serif";
const FONT_DECO = "'Cinzel', serif";
const FONT_HUD  = "'Orbitron', monospace";

// ── Star Display ──────────────────────────────────────────────────────────────
function StarDisplay({ score, color = '#d4a843', size = 'sm' }: { score: number; color?: string; size?: 'xs' | 'sm' }) {
  const sz = size === 'xs' ? 'text-[10px]' : 'text-xs';
  return (
    <div className="flex items-center gap-px">
      {[1, 2, 3, 4, 5].map(s => (
        <i key={s}
          className={`${s <= Math.round(score) ? 'ri-star-fill' : 'ri-star-line'} ${sz}`}
          style={{ color: s <= Math.round(score) ? color : '#1e2a3a' }}
        />
      ))}
    </div>
  );
}

// ── Voter Row ─────────────────────────────────────────────────────────────────
function VoterRow({ rating, targetColor }: { rating: Rating; targetColor: string }) {
  const raterColor = rating.raterTeam === 'black' ? '#d4a843' : '#e0e0e0';
  return (
    <div className="flex items-center gap-2 px-3 py-2 border-b last:border-b-0"
      style={{ borderColor: '#0e0f1c' }}>
      <div className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-black shrink-0"
        style={{ background: `${raterColor}15`, color: raterColor, border: `1px solid ${raterColor}30` }}>
        {rating.raterName.charAt(0).toUpperCase()}
      </div>
      <span className="text-[10px] flex-1 truncate" style={{ color: '#4a5a7a', fontFamily: FONT_BODY }}>
        {rating.raterName}
      </span>
      {rating.comment && (
        <span className="text-[9px] italic truncate max-w-[60px]"
          style={{ color: '#2a3a5a', fontFamily: FONT_BODY }}>「{rating.comment}」</span>
      )}
      <StarDisplay score={rating.score} color={targetColor} size="xs" />
      <span className="text-xs font-black tabular-nums w-4 text-right shrink-0"
        style={{ color: targetColor, fontFamily: FONT_HUD }}>{rating.score}</span>
    </div>
  );
}

// ── Summary Types ─────────────────────────────────────────────────────────────
interface TargetSummary {
  targetId: string; targetName: string; targetTeam: 'black' | 'white';
  avgScore: number; totalScore: number; count: number; ratings: Rating[];
}

function buildSummaries(ratings: Rating[]): TargetSummary[] {
  const map = new Map<string, TargetSummary>();
  ratings.forEach(r => {
    if (!map.has(r.targetId)) {
      map.set(r.targetId, { targetId: r.targetId, targetName: r.targetName, targetTeam: r.targetTeam, avgScore: 0, totalScore: 0, count: 0, ratings: [] });
    }
    const s = map.get(r.targetId)!;
    s.ratings.push(r);
    s.totalScore += r.score;
    s.count++;
  });
  map.forEach(s => { s.avgScore = s.totalScore / s.count; });
  return Array.from(map.values()).sort((a, b) => b.totalScore - a.totalScore);
}

function computeEntries(ratings: Rating[]): RatingResultEntry[] {
  const map = new Map<string, RatingResultEntry>();
  ratings.forEach(r => {
    if (!map.has(r.targetId)) {
      map.set(r.targetId, { targetId: r.targetId, targetName: r.targetName, targetTeam: r.targetTeam, totalScore: 0, avgScore: 0, voteCount: 0 });
    }
    const e = map.get(r.targetId)!;
    e.totalScore += r.score;
    e.voteCount++;
  });
  map.forEach(e => { e.avgScore = e.totalScore / e.voteCount; });
  return Array.from(map.values()).sort((a, b) => b.totalScore - a.totalScore);
}

// ── Team Results Tab ──────────────────────────────────────────────────────────
function TeamResultsTab({ ratings }: { ratings: Rating[] }) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const summaries = useMemo(() => buildSummaries(ratings), [ratings]);
  const blackSum  = summaries.find(s => s.targetId === 'team-black');
  const whiteSum  = summaries.find(s => s.targetId === 'team-white');
  const totalVoters = useMemo(() => new Set(ratings.map(r => r.raterId)).size, [ratings]);
  const leading = summaries[0];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-black tracking-[0.35em] uppercase" style={{ color: '#2a3a5a', fontFamily: FONT_DECO }}>
          隊伍評分結果
        </p>
        <span className="text-[10px] px-2 py-0.5 rounded-full"
          style={{ background: 'rgba(212,168,67,0.08)', color: '#d4a84370', border: '1px solid rgba(212,168,67,0.18)', fontFamily: FONT_BODY }}>
          {totalVoters} 人已投票
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {[
          { id: 'team-black', name: '黑隊', color: '#d4a843', rgb: '212,168,67', icon: 'ri-shield-fill', sum: blackSum },
          { id: 'team-white', name: '白隊', color: '#e0e0e0', rgb: '224,224,224', icon: 'ri-sword-fill', sum: whiteSum },
        ].map(team => {
          const total = team.sum?.totalScore ?? 0;
          const avg   = team.sum?.avgScore ?? 0;
          const cnt   = team.sum?.count ?? 0;
          const isWin = leading?.targetId === team.id && cnt > 0;
          return (
            <div key={team.id}
              className="rounded-2xl p-3 relative cursor-pointer transition-all"
              onClick={() => setExpanded(expanded === team.id ? null : team.id)}
              style={{
                background: isWin ? `linear-gradient(160deg, rgba(${team.rgb},0.15), rgba(${team.rgb},0.04))` : 'linear-gradient(160deg, #0c0d1a, #08090f)',
                border: `1.5px solid rgba(${team.rgb},${isWin ? 0.5 : 0.15})`,
                boxShadow: isWin ? `0 0 20px rgba(${team.rgb},0.15)` : 'none',
              }}>
              {isWin && (
                <i className="ri-trophy-fill text-xs absolute top-2.5 right-2.5"
                  style={{ color: team.color, filter: `drop-shadow(0 0 6px ${team.color}80)` }} />
              )}
              <div className="flex items-center gap-1.5 mb-2">
                <i className={`${team.icon} text-sm`} style={{ color: team.color }} />
                <span className="text-xs font-black" style={{ color: team.color, fontFamily: FONT_BODY }}>{team.name}</span>
              </div>
              <div className="flex items-end gap-1.5 mb-0.5">
                <span className="font-black text-3xl tabular-nums leading-none"
                  style={{ color: team.color, fontFamily: FONT_HUD }}>
                  {total > 0 ? total : '—'}
                </span>
                {total > 0 && (
                  <span className="text-[10px] mb-1" style={{ color: `rgba(${team.rgb},0.5)`, fontFamily: FONT_BODY }}>分</span>
                )}
              </div>
              {avg > 0 && (
                <div className="flex items-center gap-1.5 mb-1">
                  <StarDisplay score={avg} color={team.color} />
                  <span className="text-[9px]" style={{ color: `rgba(${team.rgb},0.5)`, fontFamily: FONT_BODY }}>均{avg.toFixed(1)}</span>
                </div>
              )}
              <div className="flex items-center justify-between mt-1">
                <p className="text-[9px]" style={{ color: `rgba(${team.rgb},0.4)`, fontFamily: FONT_BODY }}>
                  {cnt > 0 ? `${cnt} 票` : '未有評分'}
                </p>
                {cnt > 0 && (
                  <i className={`ri-arrow-${expanded === team.id ? 'up' : 'down'}-s-line text-xs`}
                    style={{ color: `rgba(${team.rgb},0.4)` }} />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {[
        { id: 'team-black', name: '黑隊', color: '#d4a843', rgb: '212,168,67', sum: blackSum },
        { id: 'team-white', name: '白隊', color: '#e0e0e0', rgb: '224,224,224', sum: whiteSum },
      ].map(team => (
        expanded === team.id && team.sum && team.sum.ratings.length > 0 && (
          <div key={team.id} className="rounded-xl overflow-hidden"
            style={{ border: `1px solid rgba(${team.rgb},0.2)`, background: '#080914' }}>
            <div className="px-3 py-2 border-b flex items-center justify-between"
              style={{ borderColor: `rgba(${team.rgb},0.12)`, background: `rgba(${team.rgb},0.06)` }}>
              <span className="text-[10px] font-black" style={{ color: team.color, fontFamily: FONT_DECO }}>
                {team.name} 票數明細
              </span>
              <span className="text-[9px]" style={{ color: `rgba(${team.rgb},0.5)`, fontFamily: FONT_HUD }}>
                總{team.sum.totalScore} · 均{team.sum.avgScore.toFixed(1)}
              </span>
            </div>
            {team.sum.ratings
              .slice()
              .sort((a, b) => b.score - a.score)
              .map(r => <VoterRow key={r.id} rating={r} targetColor={team.color} />)}
          </div>
        )
      ))}
    </div>
  );
}

// ── Individual Results Tab ────────────────────────────────────────────────────
function IndividualResultsTab({ ratings, members }: { ratings: Rating[]; members: ReturnType<typeof useScore>['members'] }) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const summaries   = useMemo(() => buildSummaries(ratings), [ratings]);
  const totalVoters = useMemo(() => new Set(ratings.map(r => r.raterId)).size, [ratings]);

  const blackSums = summaries.filter(s => s.targetTeam === 'black');
  const whiteSums = summaries.filter(s => s.targetTeam === 'white');
  const ratedIds  = new Set(summaries.map(s => s.targetId));
  const unvotedBlack = members.filter(m => m.team === 'black' && !ratedIds.has(m.id));
  const unvotedWhite = members.filter(m => m.team === 'white' && !ratedIds.has(m.id));

  const renderGroup = (
    group: TargetSummary[],
    unvoted: typeof members,
    label: string,
    color: string,
    rgb: string,
  ) => (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-black tracking-widest uppercase"
          style={{ color, fontFamily: FONT_DECO }}>{label}</span>
        <div className="h-px flex-1" style={{ background: `rgba(${rgb},0.15)` }} />
        <span className="text-[9px]" style={{ color: `rgba(${rgb},0.4)`, fontFamily: FONT_BODY }}>
          {group.length + unvoted.length} 人
        </span>
      </div>

      {group.map((s, idx) => {
        const isTop      = summaries.indexOf(s) === 0;
        const isExpanded = expanded === s.targetId;
        const rank       = summaries.indexOf(s) + 1;
        return (
          <div key={s.targetId} className="rounded-xl overflow-hidden"
            style={{ border: `1px solid rgba(${rgb},${isTop ? 0.35 : 0.1})` }}>
            <button
              className="w-full px-3 py-2.5 flex items-center gap-2.5 cursor-pointer"
              onClick={() => setExpanded(isExpanded ? null : s.targetId)}
              style={{
                background: isTop
                  ? `linear-gradient(135deg, rgba(${rgb},0.12), #08090f)`
                  : idx % 2 === 0 ? '#09090f' : '#080810',
              }}>
              <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0"
                style={{ background: isTop ? color : `rgba(${rgb},0.08)`, border: `1px solid rgba(${rgb},${isTop ? 1 : 0.15})` }}>
                {isTop
                  ? <i className="ri-trophy-fill text-[9px]" style={{ color: '#06060f' }} />
                  : <span className="text-[8px] font-black" style={{ color: `rgba(${rgb},0.5)`, fontFamily: FONT_HUD }}>{rank}</span>
                }
              </div>

              <span className="text-xs font-black flex-1 text-left truncate"
                style={{ color: isTop ? 'white' : '#555', fontFamily: FONT_BODY }}>
                {s.targetName}
              </span>

              <div className="flex items-center gap-1.5 shrink-0">
                <span className="text-sm font-black tabular-nums"
                  style={{ color, fontFamily: FONT_HUD }}>{s.totalScore}</span>
                <span className="text-[9px]" style={{ color: `rgba(${rgb},0.35)`, fontFamily: FONT_BODY }}>分</span>
                <span className="text-[8px]" style={{ color: `rgba(${rgb},0.25)`, fontFamily: FONT_BODY }}>·</span>
                <span className="text-[9px]" style={{ color: `rgba(${rgb},0.35)`, fontFamily: FONT_BODY }}>{s.count}票</span>
                <i className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line text-xs`}
                  style={{ color: `rgba(${rgb},0.3)` }} />
              </div>
            </button>

            {isExpanded && s.ratings.length > 0 && (
              <div style={{ background: '#060710', borderTop: `1px solid rgba(${rgb},0.1)` }}>
                <div className="px-3 py-1.5 flex items-center gap-3"
                  style={{ borderBottom: `1px solid rgba(${rgb},0.06)` }}>
                  <span className="text-[9px]" style={{ color: `rgba(${rgb},0.4)`, fontFamily: FONT_BODY }}>
                    均分 {s.avgScore.toFixed(1)}
                  </span>
                  <StarDisplay score={s.avgScore} color={color} size="xs" />
                </div>
                {s.ratings
                  .slice()
                  .sort((a, b) => b.score - a.score)
                  .map(r => <VoterRow key={r.id} rating={r} targetColor={color} />)}
              </div>
            )}
          </div>
        );
      })}

      {unvoted.map(m => (
        <div key={m.id} className="rounded-xl px-3 py-2 flex items-center gap-2"
          style={{ background: '#07080e', border: '1px solid #12131e' }}>
          <div className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] shrink-0"
            style={{ background: `rgba(${rgb},0.06)`, color: `rgba(${rgb},0.3)` }}>—</div>
          <span className="text-[10px] flex-1 truncate" style={{ color: '#2a3a5a', fontFamily: FONT_BODY }}>{m.name}</span>
          <span className="text-[9px]" style={{ color: '#1a2030', fontFamily: FONT_BODY }}>尚無評分</span>
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-black tracking-[0.35em] uppercase" style={{ color: '#2a3a5a', fontFamily: FONT_DECO }}>
          個人評分結果
        </p>
        <span className="text-[10px] px-2 py-0.5 rounded-full"
          style={{ background: 'rgba(212,168,67,0.08)', color: '#d4a84370', border: '1px solid rgba(212,168,67,0.18)', fontFamily: FONT_BODY }}>
          {totalVoters} 人已投票
        </span>
      </div>
      {renderGroup(blackSums, unvotedBlack, '黑隊', '#d4a843', '212,168,67')}
      {renderGroup(whiteSums, unvotedWhite, '白隊', '#e0e0e0', '224,224,224')}
    </div>
  );
}

// ── Control Tab ────────────────────────────────────────────────────────────────
function ControlTab({
  ratingActive,
  votingType,
  votingTitle,
  totalRaters,
  items,
  onActivateItem,
  onDeactivate,
  onClear,
  onGoToResults,
  onAddItem,
  onDeleteItem,
  hasRatings,
}: {
  ratingActive: boolean;
  votingType: 'team' | 'individual' | undefined;
  votingTitle: string | undefined;
  totalRaters: number;
  items: RatingItem[];
  onActivateItem: (item: RatingItem) => void;
  onDeactivate: () => void;
  onClear: () => void;
  onGoToResults: () => void;
  onAddItem: (title: string, type: 'team' | 'individual') => void;
  onDeleteItem: (id: string) => void;
  hasRatings: boolean;
}) {
  const [addOpen, setAddOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newType, setNewType]   = useState<'team' | 'individual'>('individual');
  const [delConfirm, setDelConfirm] = useState<string | null>(null);

  const handleAdd = () => {
    if (!newTitle.trim()) return;
    onAddItem(newTitle.trim(), newType);
    setNewTitle('');
    setAddOpen(false);
  };

  return (
    <div className="space-y-4">

      {/* ── Active session status ── */}
      {ratingActive && (
        <div className="rounded-xl p-3 border border-[#1a2040] relative overflow-hidden"
          style={{ background: 'rgba(126,200,160,0.04)' }}>
          <div className="absolute top-0 left-0 right-0 h-px"
            style={{ background: 'linear-gradient(90deg, transparent, rgba(126,200,160,0.4), transparent)' }} />
          <div className="flex items-center gap-2 mb-1.5">
            <span className="w-1.5 h-1.5 rounded-full animate-pulse shrink-0" style={{ background: '#7ec8a0' }} />
            <i className={`${votingType === 'team' ? 'ri-shield-fill' : 'ri-user-star-fill'} text-xs`} style={{ color: '#7ec8a0' }} />
            <span className="text-xs font-bold flex-1 truncate" style={{ color: '#7ec8a0', fontFamily: FONT_BODY }}>
              {votingTitle || (votingType === 'team' ? '隊伍評分' : '個人評分')}
            </span>
            <span className="text-[9px]" style={{ color: '#3a5a50', fontFamily: FONT_BODY }}>{totalRaters} 人</span>
          </div>
          <p className="text-[10px] mb-2.5" style={{ color: '#2a4060', fontFamily: FONT_BODY }}>
            玩家導覽列現在可見「評分」按鈕
          </p>
          <div className="flex gap-2">
            {hasRatings && (
              <button onClick={onGoToResults}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold cursor-pointer whitespace-nowrap"
                style={{ background: 'rgba(212,168,67,0.15)', color: '#d4a843', border: '1px solid rgba(212,168,67,0.35)', fontFamily: FONT_BODY }}>
                <i className="ri-bar-chart-2-line text-xs" />前往結算
              </button>
            )}
            <button onClick={onDeactivate}
              className={`${hasRatings ? '' : 'flex-1'} flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs cursor-pointer whitespace-nowrap`}
              style={{ color: '#e06060', border: '1px solid rgba(224,96,96,0.25)', background: 'rgba(224,96,96,0.06)', fontFamily: FONT_BODY }}>
              <i className="ri-stop-circle-line text-xs" />關閉評分
            </button>
            {hasRatings && (
              <button onClick={onClear}
                className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs cursor-pointer whitespace-nowrap"
                style={{ color: '#2a3a5a', border: '1px solid #1e2035', fontFamily: FONT_BODY }}>
                <i className="ri-delete-bin-line text-xs" />清空
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Add new item ── */}
      <div className="rounded-xl overflow-hidden"
        style={{ border: `1px solid ${addOpen ? 'rgba(212,168,67,0.35)' : '#1a1a2e'}`, transition: 'border-color 0.2s' }}>
        <button
          onClick={() => setAddOpen(o => !o)}
          className="w-full flex items-center gap-2.5 px-3 py-2.5 cursor-pointer transition-all"
          style={{ background: addOpen ? 'rgba(212,168,67,0.06)' : '#09090f' }}>
          <div className="w-6 h-6 rounded-lg flex items-center justify-center shrink-0"
            style={{
              background: addOpen ? 'rgba(212,168,67,0.2)' : 'rgba(212,168,67,0.06)',
              border: `1px solid rgba(212,168,67,${addOpen ? 0.5 : 0.15})`,
              transition: 'all 0.2s',
            }}>
            <i className={`${addOpen ? 'ri-close-line' : 'ri-add-line'} text-xs`} style={{ color: '#d4a843' }} />
          </div>
          <span className="text-xs font-bold" style={{ color: addOpen ? '#d4a843' : '#3a4a5a', fontFamily: FONT_BODY }}>
            新增評分項目
          </span>
        </button>

        {addOpen && (
          <div className="px-3 pb-3 space-y-3 border-t" style={{ borderColor: 'rgba(212,168,67,0.15)' }}>
            <div className="pt-3">
              <p className="text-[9px] font-black tracking-[0.3em] uppercase mb-2"
                style={{ color: '#2a3a5a', fontFamily: FONT_DECO }}>評分對象</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: 'team' as const, label: '投敵方隊伍', icon: 'ri-shield-fill' },
                  { value: 'individual' as const, label: '投敵方成員', icon: 'ri-user-star-fill' },
                ].map(opt => {
                  const sel = newType === opt.value;
                  return (
                    <button key={opt.value} onClick={() => setNewType(opt.value)}
                      className="rounded-xl px-3 py-2 flex items-center gap-2 cursor-pointer transition-all border"
                      style={{
                        background: sel ? 'rgba(212,168,67,0.1)' : '#0a0b16',
                        borderColor: sel ? 'rgba(212,168,67,0.4)' : '#16172a',
                      }}>
                      <i className={`${opt.icon} text-sm`} style={{ color: sel ? '#d4a843' : '#2a3a5a' }} />
                      <span className="text-xs font-black" style={{ color: sel ? 'white' : '#444', fontFamily: FONT_BODY }}>
                        {opt.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <p className="text-[9px] font-black tracking-[0.3em] uppercase mb-1.5"
                style={{ color: '#2a3a5a', fontFamily: FONT_DECO }}>項目名稱</p>
              <div className="flex items-center gap-2 rounded-xl px-3 py-2"
                style={{ background: '#0a0b16', border: '1px solid #16172a' }}>
                <i className="ri-trophy-line text-[#2a3a5a] text-sm shrink-0" />
                <input
                  type="text"
                  value={newTitle}
                  onChange={e => setNewTitle(e.target.value.slice(0, 20))}
                  onKeyDown={e => e.key === 'Enter' && handleAdd()}
                  placeholder="例：最佳表現獎、最佳默契獎…"
                  className="bg-transparent text-white text-xs w-full outline-none placeholder:text-[#1e2035]"
                  style={{ fontFamily: FONT_BODY }}
                />
              </div>
            </div>

            <button
              onClick={handleAdd}
              disabled={!newTitle.trim()}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold cursor-pointer transition-all whitespace-nowrap disabled:opacity-30 disabled:cursor-not-allowed"
              style={{ background: 'rgba(212,168,67,0.15)', color: '#d4a843', border: '1px solid rgba(212,168,67,0.35)', fontFamily: FONT_BODY }}>
              <i className="ri-add-circle-line" />新增項目
            </button>
          </div>
        )}
      </div>

      {/* ── Items list ── */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] font-black tracking-[0.3em] uppercase"
            style={{ color: '#2a3a5a', fontFamily: FONT_DECO }}>評分項目</p>
          {items.length > 0 && (
            <span className="text-[9px]" style={{ color: '#1e2a38', fontFamily: FONT_BODY }}>{items.length} 個項目</span>
          )}
        </div>

        {items.length === 0 ? (
          <div className="rounded-xl px-4 py-5 text-center"
            style={{ background: '#07080e', border: '1px dashed #1a1a2e' }}>
            <i className="ri-list-check-3 text-2xl block mb-1.5" style={{ color: '#1e2a38' }} />
            <p className="text-[11px]" style={{ color: '#1e2a38', fontFamily: FONT_BODY }}>尚未建立評分項目</p>
            <p className="text-[10px] mt-0.5" style={{ color: '#141a24', fontFamily: FONT_BODY }}>
              点擊上方「+ 新增」開始建立</p>
          </div>
        ) : (
          <div className="space-y-1.5">
            {items.map(item => {
              const isActive  = ratingActive && votingTitle === item.title && votingType === item.votingType;
              const typeColor = item.votingType === 'team' ? '#d4a843' : '#7ec8a0';
              const typeRgb   = item.votingType === 'team' ? '212,168,67' : '126,200,160';
              const typeLabel = item.votingType === 'team' ? '隊伍' : '個人';
              const typeIcon  = item.votingType === 'team' ? 'ri-shield-fill' : 'ri-user-star-fill';
              const isDel     = delConfirm === item.id;

              return (
                <div key={item.id}
                  className="rounded-xl transition-all"
                  style={{
                    background: isActive
                      ? `linear-gradient(135deg, rgba(${typeRgb},0.12), rgba(${typeRgb},0.04))`
                      : 'linear-gradient(135deg, #0a0b16, #080910)',
                    border: `1.5px solid rgba(${typeRgb},${isActive ? 0.5 : 0.12})`,
                    boxShadow: isActive ? `0 0 16px rgba(${typeRgb},0.15)` : 'none',
                  }}>
                  <div className="px-3 py-2.5 flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                      style={{ background: `rgba(${typeRgb},0.12)`, border: `1px solid rgba(${typeRgb},0.2)` }}>
                      <i className={`${typeIcon} text-xs`} style={{ color: typeColor }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-black truncate"
                        style={{ color: isActive ? 'white' : '#666', fontFamily: FONT_BODY }}>
                        {item.title || '（未命名）'}
                      </p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-[8px] font-black px-1.5 py-px rounded"
                          style={{
                            background: `rgba(${typeRgb},0.1)`,
                            color: `rgba(${typeRgb},0.7)`,
                            border: `1px solid rgba(${typeRgb},0.18)`,
                            fontFamily: FONT_DECO,
                          }}>
                          {typeLabel}
                        </span>
                        {isActive && (
                          <span className="text-[8px] font-black px-1.5 py-px rounded animate-pulse"
                            style={{ background: 'rgba(126,200,160,0.12)', color: '#7ec8a0', border: '1px solid rgba(126,200,160,0.25)', fontFamily: FONT_BODY }}>
                            進行中
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {isDel ? (
                        <>
                          <button onClick={() => onDeleteItem(item.id)}
                            className="text-[9px] px-2 py-1 rounded-lg cursor-pointer font-black whitespace-nowrap"
                            style={{ background: 'rgba(220,50,50,0.15)', color: '#e06060', border: '1px solid rgba(220,50,50,0.3)' }}>
                            確認
                          </button>
                          <button onClick={() => setDelConfirm(null)}
                            className="text-[9px] px-2 py-1 rounded-lg cursor-pointer whitespace-nowrap"
                            style={{ background: 'rgba(255,255,255,0.04)', color: '#444' }}>
                            取消
                          </button>
                        </>
                      ) : (
                        <>
                          {!isActive && (
                            <button
                              onClick={() => onActivateItem(item)}
                              disabled={ratingActive}
                              className="text-[9px] px-2.5 py-1.5 rounded-lg cursor-pointer font-black whitespace-nowrap disabled:opacity-30 disabled:cursor-not-allowed"
                              style={{
                                background: `rgba(${typeRgb},0.15)`,
                                color: typeColor,
                                border: `1px solid rgba(${typeRgb},0.35)`,
                                fontFamily: FONT_BODY,
                              }}>
                              <i className="ri-play-circle-line mr-1" />啟動
                            </button>
                          )}
                          {!isActive && (
                            <button onClick={() => setDelConfirm(item.id)}
                              className="w-7 h-7 flex items-center justify-center rounded-lg cursor-pointer transition-all shrink-0"
                              style={{ background: 'rgba(255,255,255,0.03)', color: '#2a3040' }}>
                              <i className="ri-delete-bin-line text-xs" />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
// ── Archive Card ──────────────────────────────────────────────────────────────
function ArchiveCard({
  archive,
  onDelete,
}: {
  archive: RatingArchive;
  onDelete: (id: string) => void;
}) {
  const [expanded, setExpanded]     = useState(false);
  const [delConfirm, setDelConfirm] = useState(false);

  const summaries = useMemo(() => {
    const map = new Map<string, { targetId: string; targetName: string; targetTeam: 'black' | 'white'; avgScore: number; totalScore: number; count: number; ratings: Rating[] }>();
    archive.ratingsSnapshot.forEach(r => {
      if (!map.has(r.targetId)) {
        map.set(r.targetId, { targetId: r.targetId, targetName: r.targetName, targetTeam: r.targetTeam, avgScore: 0, totalScore: 0, count: 0, ratings: [] });
      }
      const s = map.get(r.targetId)!;
      s.ratings.push(r);
      s.totalScore += r.score;
      s.count++;
    });
    map.forEach(s => { s.avgScore = s.totalScore / s.count; });
    return Array.from(map.values()).sort((a, b) => b.totalScore - a.totalScore);
  }, [archive]);

  return (
    <div className="rounded-2xl overflow-hidden"
      style={{ background: 'linear-gradient(160deg, #0c0d1a, #080910)', border: '1px solid #1a1a2e' }}>

      <div className="px-4 py-3 flex items-center gap-3">
        <div className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: 'rgba(212,168,67,0.15)', border: '1px solid rgba(212,168,67,0.3)' }}>
          <i className="ri-history-line text-sm" style={{ color: '#d4a843' }} />
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-xs font-black truncate text-white" style={{ fontFamily: FONT_BODY }}>
            {archive.title}
          </p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[9px] px-1.5 py-px rounded font-black"
              style={{
                background: archive.votingType === 'team' ? 'rgba(212,168,67,0.1)' : 'rgba(126,200,160,0.08)',
                color: archive.votingType === 'team' ? '#d4a84380' : '#7ec8a080',
                border: `1px solid ${archive.votingType === 'team' ? 'rgba(212,168,67,0.2)' : 'rgba(126,200,160,0.15)'}`,
                fontFamily: FONT_DECO,
              }}>
              {archive.votingType === 'team' ? 'TEAM' : 'INDIV'}
            </span>
          </div>
        </div>

        {/* Top scorer badge */}
        {summaries[0] && (
          <div className="flex items-center gap-1 px-2 py-1 rounded-lg shrink-0"
            style={{ background: 'rgba(212,168,67,0.08)', border: '1px solid rgba(212,168,67,0.15)' }}>
            <i className="ri-trophy-fill text-[9px]" style={{ color: '#d4a843' }} />
            <span className="text-[9px] font-black truncate max-w-[50px]"
              style={{ color: '#d4a843', fontFamily: FONT_BODY }}>{summaries[0].targetName}</span>
            <span className="text-[9px] font-black tabular-nums"
              style={{ color: '#d4a84380', fontFamily: FONT_HUD }}>{summaries[0].totalScore}分</span>
          </div>
        )}

        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={() => setExpanded(e => !e)}
            className="w-7 h-7 flex items-center justify-center rounded-lg cursor-pointer transition-all"
            style={{ background: expanded ? 'rgba(212,168,67,0.1)' : 'rgba(255,255,255,0.04)', color: expanded ? '#d4a843' : '#333' }}>
            <i className={`ri-arrow-${expanded ? 'up' : 'down'}-s-line text-xs`} />
          </button>
          {!delConfirm ? (
            <button
              onClick={() => setDelConfirm(true)}
              className="w-7 h-7 flex items-center justify-center rounded-lg cursor-pointer transition-all"
              style={{ background: 'rgba(255,255,255,0.04)', color: '#333' }}>
              <i className="ri-delete-bin-line text-xs" />
            </button>
          ) : (
            <div className="flex items-center gap-1">
              <button
                onClick={() => onDelete(archive.id)}
                className="text-[9px] px-2 py-1 rounded-lg cursor-pointer font-black whitespace-nowrap"
                style={{ background: 'rgba(220,50,50,0.15)', color: '#e06060', border: '1px solid rgba(220,50,50,0.3)' }}>
                確認
              </button>
              <button
                onClick={() => setDelConfirm(false)}
                className="text-[9px] px-2 py-1 rounded-lg cursor-pointer whitespace-nowrap"
                style={{ background: 'rgba(255,255,255,0.04)', color: '#444' }}>
                取消
              </button>
            </div>
          )}
        </div>
      </div>

      {expanded && (
        <div className="border-t" style={{ borderColor: '#12131e' }}>
          {summaries.map((s, idx) => {
            const color = s.targetTeam === 'black' ? '#d4a843' : '#e0e0e0';
            const rgb   = s.targetTeam === 'black' ? '212,168,67' : '224,224,224';
            const isTop = idx === 0;
            return (
              <div key={s.targetName + s.targetTeam}
                className="px-4 py-2.5 flex items-center gap-3 border-b last:border-b-0"
                style={{
                  borderColor: '#0e0f1c',
                  background: isTop ? `rgba(${rgb},0.05)` : 'transparent',
                }}>
                <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0"
                  style={{
                    background: isTop ? color : `rgba(${rgb},0.06)`,
                    border: `1px solid rgba(${rgb},${isTop ? 1 : 0.15})`,
                  }}>
                  {isTop
                    ? <i className="ri-trophy-fill text-[8px]" style={{ color: '#06060f' }} />
                    : <span className="text-[8px] font-black" style={{ color: `rgba(${rgb},0.4)`, fontFamily: FONT_HUD }}>{idx + 1}</span>
                  }
                </div>

                <span className="text-xs font-black flex-1 truncate"
                  style={{ color: isTop ? 'white' : '#555', fontFamily: FONT_BODY }}>
                {s.targetName}
                </span>

                <div className="flex items-center gap-1 flex-wrap justify-end max-w-[120px]">
                  {s.ratings.map(r => {
                    const rc = r.raterTeam === 'black' ? '#d4a843' : '#e0e0e0';
                    return (
                      <span key={r.id}
                        className="text-[8px] px-1.5 py-px rounded-full"
                        style={{ background: `${rc}10`, color: `${rc}80`, border: `1px solid ${rc}18`, fontFamily: FONT_BODY }}>
                        {r.score}★
                      </span>
                    );
                  })}
                </div>

                {/* Show total score as primary */}
                <div className="text-right shrink-0">
                  <span className="text-sm font-black tabular-nums block"
                    style={{ color, fontFamily: FONT_HUD }}>
                    {s.totalScore}
                  </span>
                  <span className="text-[8px]" style={{ color: `rgba(${rgb},0.35)`, fontFamily: FONT_BODY }}>
                    均{s.avgScore.toFixed(1)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Voter Status Grid ─────────────────────────────────────────────────────────
function VoterStatusGrid({
  members,
  ratings,
}: {
  members: ReturnType<typeof useScore>['members'];
  ratings: Rating[];
}) {
  const votedIds    = useMemo(() => new Set(ratings.map(r => r.raterId)), [ratings]);
  const votedCount  = useMemo(() => members.filter(m => votedIds.has(m.id)).length, [members, votedIds]);
  const total       = members.length;
  const pct         = total > 0 ? Math.round((votedCount / total) * 100) : 0;
  const allDone     = votedCount >= total && total > 0;

  return (
    <div className="rounded-xl p-3" style={{ background: '#07080e', border: '1px solid #14152a' }}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[9px] font-black tracking-[0.3em] uppercase"
          style={{ color: '#2a3a5a', fontFamily: FONT_DECO }}>
          即時投票進度
        </span>
        <span className="text-[10px] font-black tabular-nums"
          style={{ color: allDone ? '#7ec8a0' : '#d4a843', fontFamily: FONT_HUD }}>
          {votedCount} / {total}
        </span>
      </div>

      <div className="h-1 rounded-full mb-3 overflow-hidden" style={{ background: 'rgba(212,168,67,0.06)' }}>
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{
            width: `${pct}%`,
            background: allDone
              ? 'linear-gradient(90deg,#7ec8a0,#5daf87)'
              : 'linear-gradient(90deg,#d4a843,#b8862e)',
          }}
        />
      </div>

      {members.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {members.map(m => {
            const voted = votedIds.has(m.id);
            const color = m.team === 'black' ? '#d4a843' : '#e0e0e0';
            const rgb   = m.team === 'black' ? '212,168,67' : '224,224,224';
            return (
              <div
                key={m.id}
                className="flex items-center gap-1 px-2 py-1 rounded-lg transition-all duration-300"
                style={{
                  background: voted ? `rgba(${rgb},0.12)` : 'rgba(255,255,255,0.02)',
                  border: `1px solid rgba(${rgb},${voted ? 0.32 : 0.07})`,
                }}
              >
                <span
                  className="text-[9px] font-black truncate max-w-[44px]"
                  style={{ color: voted ? color : '#2a3a4a', fontFamily: FONT_BODY }}
                >
                  {m.name}
                </span>
                {voted ? (
                  <i className="ri-checkbox-circle-fill text-[9px]" style={{ color: '#7ec8a0' }} />
                ) : (
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ background: 'rgba(255,255,255,0.08)', animation: 'panelDot 2s ease-in-out infinite' }}
                  />
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-[10px] text-center py-1" style={{ color: '#1e2a38', fontFamily: FONT_BODY }}>
          等待玩家加入…
        </p>
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function RatingPanel() {
  const { scheduleState, setRatingActive, setVotingConfig, members, setShowJudgePanel } = useScore();
  const { ratingActive, votingType, votingTitle } = scheduleState;
  const { ratings, loading, clearAllRatings } = useRatings();
  const { archives, archiveSession, deleteArchive } = useRatingArchives();
  const { items, addItem, deleteItem } = useRatingItems();
  const { publishResult } = useRatingResultBroadcast();

  const [settling, setSettling] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [tab, setTab] = useState<'control' | 'results' | 'history'>('control');

  const totalRaters = useMemo(() => new Set(ratings.map(r => r.raterId)).size, [ratings]);
  const hasRatings  = ratings.length > 0;

  // Auto-switch to results tab when first rating arrives
  const prevHasRatings = useRef(hasRatings);
  useEffect(() => {
    if (!prevHasRatings.current && hasRatings) {
      setTab('results');
    }
    prevHasRatings.current = hasRatings;
  }, [hasRatings]);

  // Auto-open history when a new archive is added
  const prevArchiveCount = useRef(archives.length);
  useEffect(() => {
    if (archives.length > prevArchiveCount.current) {
      setHistoryOpen(true);
    }
    prevArchiveCount.current = archives.length;
  }, [archives.length]);

  const [activateConfirm, setActivateConfirm] = useState<RatingItem | null>(null);

  const handleActivateItem = (item: RatingItem) => {
    // If there are leftover ratings from a previous session, warn before activating
    if (hasRatings) {
      setActivateConfirm(item);
      return;
    }
    setVotingConfig(item.votingType, item.title);
    setTimeout(() => setRatingActive(true), 100);
  };

  const handleActivateConfirmed = (item: RatingItem, clearFirst: boolean) => {
    setActivateConfirm(null);
    if (clearFirst) {
      clearAllRatings().then(() => {
        setVotingConfig(item.votingType, item.title);
        setTimeout(() => setRatingActive(true), 100);
      });
    } else {
      setVotingConfig(item.votingType, item.title);
      setTimeout(() => setRatingActive(true), 100);
    }
  };

  // Simple close — no archive
  const handleDeactivate = () => {
    setRatingActive(false);
    setTab('control');
  };

  // Settle: archive + broadcast to players + clear + close → jump to history
  const handleSettle = async () => {
    if (!hasRatings || settling) return;
    setSettling(true);
    try {
      const type            = votingType ?? 'individual';
      const title           = votingTitle ?? '評分記錄';
      const snapshotRatings = [...ratings];
      const now             = Date.now();

      await archiveSession(title, type, snapshotRatings);

      // Broadcast computed results to all players
      publishResult({
        id: `archive-${now}`,
        title,
        votingType: type,
        archivedAt: now,
        totalVoters: new Set(snapshotRatings.map(r => r.raterId)).size,
        entries: computeEntries(snapshotRatings),
      });

      await clearAllRatings();
      setRatingActive(false);
      setTab('history');
    } finally {
      setSettling(false);
    }
  };

  const activeType = votingType ?? 'individual';

  return (
    <div className="flex flex-col">

      {/* ── Panel Header ─────────────────────────────── */}
      <div className="px-4 pt-4 pb-0">
        <div className="rounded-2xl overflow-hidden border border-[#1e1030]"
          style={{ background: 'linear-gradient(160deg, #0d0a18, #090810)' }}>
          <div className="px-4 py-3 flex items-center gap-2.5"
            style={{ background: 'linear-gradient(90deg, rgba(212,168,67,0.08), transparent 60%)' }}>
            <div className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: 'rgba(212,168,67,0.15)', border: '1px solid rgba(212,168,67,0.3)' }}>
              <i className="ri-star-fill text-sm" style={{ color: '#d4a843' }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-xs font-bold" style={{ fontFamily: FONT_BODY }}>玩家評分系統</p>
              <p className="text-[10px]" style={{ color: '#3a2a4a', fontFamily: FONT_BODY }}>
                {ratingActive
                  ? `進行中 · ${totalRaters} / ${members.length} 人已評分 · ${votingType === 'team' ? '隊伍模式' : '個人模式'}`
                  : hasRatings ? `已結束 · ${totalRaters} 人完成評分` : `${items.length} 個評分項目 · 尚未啟動`}
              </p>
            </div>
            {ratingActive ? (
              <span className="text-[9px] px-2 py-1 rounded-full font-black shrink-0 whitespace-nowrap animate-pulse"
                style={{ background: 'rgba(126,200,160,0.12)', color: '#7ec8a0', border: '1px solid rgba(126,200,160,0.25)', fontFamily: FONT_BODY }}>
                進行中
              </span>
            ) : hasRatings ? (
              <span className="text-[9px] px-2 py-1 rounded-full font-black shrink-0 whitespace-nowrap"
                style={{ background: 'rgba(212,168,67,0.08)', color: '#d4a84370', border: '1px solid rgba(212,168,67,0.2)', fontFamily: FONT_BODY }}>
                已結束
              </span>
            ) : null}
          </div>

          {/* Tab bar — 3 tabs */}
          <div className="flex border-t" style={{ borderColor: '#1a1030' }}>
            {[
              { key: 'control',  label: `項目${items.length > 0 ? ` · ${items.length}` : ''}`,       icon: 'ri-list-check-3' },
              { key: 'results',  label: `即時結果${hasRatings ? ` · ${totalRaters}人` : ''}`,          icon: 'ri-bar-chart-2-line' },
              { key: 'history',  label: `歷史紀錄${archives.length > 0 ? ` · ${archives.length}` : ''}`, icon: 'ri-history-line' },
            ].map(t => {
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key as typeof tab)}
                  className="flex-1 flex items-center justify-center gap-1 py-2.5 text-[10px] font-bold cursor-pointer transition-all whitespace-nowrap"
                  style={{
                    color: active ? '#d4a843' : '#2a3a5a',
                    borderBottom: active ? '2px solid #d4a843' : '2px solid transparent',
                    background: active ? 'rgba(212,168,67,0.06)' : 'transparent',
                    fontFamily: FONT_BODY,
                  }}>
                  <i className={`${t.icon} text-xs`} />
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Tab Content ──────────────────────────────── */}
      <div className="px-4 py-4">

        {/* ── 項目 tab ── */}
        {tab === 'control' && (
          <>
            <ControlTab
              ratingActive={!!ratingActive}
              votingType={votingType}
              votingTitle={votingTitle}
              totalRaters={totalRaters}
              items={items}
              onActivateItem={handleActivateItem}
              onDeactivate={handleDeactivate}
              onClear={clearAllRatings}
              onGoToResults={() => setTab('results')}
              onAddItem={addItem}
              onDeleteItem={deleteItem}
              hasRatings={hasRatings}
            />
            {/* ── Stale ratings warning dialog ── */}
            {activateConfirm && (
              <div className="fixed inset-0 z-50 flex items-center justify-center px-4"
                style={{ background: 'rgba(2,4,18,0.88)', backdropFilter: 'blur(6px)' }}>
                <div className="w-full max-w-xs rounded-2xl overflow-hidden"
                  style={{ background: 'linear-gradient(160deg,#0d0a18,#090810)', border: '1.5px solid rgba(212,168,67,0.35)' }}>
                  <div className="px-5 pt-5 pb-4">
                    <div className="flex items-center gap-2.5 mb-3">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                        style={{ background: 'rgba(212,168,67,0.15)', border: '1px solid rgba(212,168,67,0.3)' }}>
                        <i className="ri-alert-line text-base" style={{ color: '#d4a843' }} />
                      </div>
                      <div>
                        <p className="text-white text-sm font-black" style={{ fontFamily: FONT_BODY }}>有未結算的評分資料</p>
                        <p className="text-[10px] mt-0.5" style={{ color: 'rgba(212,168,67,0.5)', fontFamily: FONT_BODY }}>
                          啟動「{activateConfirm.title}」前請選擇
                        </p>
                      </div>
                    </div>
                    <p className="text-xs leading-relaxed mb-4" style={{ color: '#3a4a6a', fontFamily: FONT_BODY }}>
                      目前有 {totalRaters} 人的評分尚未結算。可以先清除舊資料再啟動，或直接啟動（舊資料會混入新結果）。
                    </p>
                    <div className="space-y-2">
                      <button
                        onClick={() => handleActivateConfirmed(activateConfirm, true)}
                        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold cursor-pointer whitespace-nowrap"
                        style={{ background: 'rgba(212,168,67,0.15)', color: '#d4a843', border: '1px solid rgba(212,168,67,0.35)', fontFamily: FONT_BODY }}>
                        <i className="ri-delete-bin-line" />清除舊資料並啟動
                      </button>
                      <button
                        onClick={() => handleActivateConfirmed(activateConfirm, false)}
                        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs cursor-pointer whitespace-nowrap"
                        style={{ color: '#4a5a7a', border: '1px solid #1e2035', fontFamily: FONT_BODY }}>
                        <i className="ri-play-circle-line" />直接啟動（保留舊資料）
                      </button>
                      <button
                        onClick={() => setActivateConfirm(null)}
                        className="w-full py-2 rounded-xl text-xs cursor-pointer whitespace-nowrap"
                        style={{ color: '#2a3040', fontFamily: FONT_BODY }}>
                        取消
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── 即時結果 tab ── */}
        {tab === 'results' && (
          <div className="space-y-4">
            {/* Live voter progress */}
            <VoterStatusGrid members={members} ratings={ratings} />

            {!loading && hasRatings ? (
              <>
                {activeType === 'team'
                  ? <TeamResultsTab ratings={ratings} />
                  : <IndividualResultsTab ratings={ratings} members={members} />}

                {/* ── Settlement button ── */}
                <div className="pt-2">
                  <div className="h-px mb-4" style={{ background: 'linear-gradient(90deg,transparent,rgba(212,168,67,0.15),transparent)' }} />

                  {/* Score summary before settling */}
                  <div className="rounded-xl p-3 mb-3" style={{ background: '#07080e', border: '1px solid rgba(212,168,67,0.12)' }}>
                    <p className="text-[9px] font-black tracking-[0.3em] uppercase mb-2"
                      style={{ color: '#2a3a5a', fontFamily: FONT_DECO }}>結算後將同步至玩家畫面</p>
                    <div className="flex items-center gap-2">
                      <i className="ri-broadcast-fill text-xs" style={{ color: '#d4a84360' }} />
                      <p className="text-[10px]" style={{ color: '#2a3a5a', fontFamily: FONT_BODY }}>
                        按下「結算此項目」後，本輪總分將即時廣播給所有玩家，並留存至歷史紀錄。
                      </p>
                    </div>
                  </div>

                  <button
                    onClick={handleSettle}
                    disabled={settling}
                    className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl text-sm font-black cursor-pointer whitespace-nowrap transition-all disabled:opacity-60 disabled:cursor-not-allowed relative overflow-hidden"
                    style={{
                      background: settling
                        ? 'rgba(212,168,67,0.15)'
                        : 'linear-gradient(135deg, rgba(212,168,67,0.25), rgba(212,168,67,0.12))',
                      color: '#d4a843',
                      border: '1.5px solid rgba(212,168,67,0.45)',
                      boxShadow: settling ? 'none' : '0 0 20px rgba(212,168,67,0.15)',
                      fontFamily: FONT_BODY,
                    }}>
                    {!settling && (
                      <div className="absolute inset-0 pointer-events-none"
                        style={{ background: 'linear-gradient(90deg,transparent,rgba(212,168,67,0.1),transparent)', animation: 'panelShim 2.5s linear infinite' }} />
                    )}
                    {settling ? (
                      <><i className="ri-loader-4-line animate-spin text-base" />結算中，廣播至玩家…</>
                    ) : (
                      <><i className="ri-check-double-line text-base" />結算此項目 · 廣播總分給玩家</>
                    )}
                  </button>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 gap-3 text-center">
                <div className="w-12 h-12 rounded-full flex items-center justify-center"
                  style={{ background: 'rgba(212,168,67,0.06)', border: '1px solid rgba(212,168,67,0.18)' }}>
                  <i className="ri-bar-chart-2-line text-xl" style={{ color: 'rgba(212,168,67,0.35)' }} />
                </div>
                <p className="text-[#2a3a5a] text-xs" style={{ fontFamily: FONT_BODY }}>
                  {ratingActive ? '等待玩家開始評分…' : '尚未啟動評分系統'}
                </p>
                {ratingActive && (
                  <div className="flex items-center gap-1">
                    {[0, 200, 400].map(d => (
                      <div key={d} className="w-1 h-1 rounded-full"
                        style={{ background: 'rgba(212,168,67,0.4)', animation: `panelDot 1.4s ${d}ms infinite` }} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── 歷史紀錄 tab ── */}
        {tab === 'history' && (
          <div className="space-y-4">
            {archives.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 gap-3 text-center">
                <div className="w-12 h-12 rounded-full flex items-center justify-center"
                  style={{ background: 'rgba(212,168,67,0.05)', border: '1px solid rgba(212,168,67,0.12)' }}>
                  <i className="ri-history-line text-xl" style={{ color: 'rgba(212,168,67,0.25)' }} />
                </div>
                <p className="text-xs" style={{ color: '#1e2a38', fontFamily: FONT_BODY }}>尚無歷史紀錄</p>
                <p className="text-[10px]" style={{ color: '#16202e', fontFamily: FONT_BODY }}>
                  完成評分並按下「結算此項目」後即可查看紀錄
                </p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <p className="text-[10px] font-black tracking-[0.3em] uppercase"
                    style={{ color: '#2a3a5a', fontFamily: FONT_DECO }}>歷史評分紀錄</p>
                  <span className="text-[9px] px-2 py-0.5 rounded-full"
                    style={{ background: 'rgba(212,168,67,0.06)', color: '#d4a84350', border: '1px solid rgba(212,168,67,0.12)', fontFamily: FONT_BODY }}>
                    {archives.length} 筆
                  </span>
                </div>

                {/* Collapsible history list */}
                <button
                  onClick={() => setHistoryOpen(o => !o)}
                  className="w-full flex items-center gap-2 cursor-pointer"
                >
                  <i className={`ri-arrow-${historyOpen ? 'up' : 'down'}-s-line text-xs`}
                    style={{ color: '#2a3050' }} />
                  <span className="text-[10px]" style={{ color: '#2a3a5a', fontFamily: FONT_BODY }}>
                    {historyOpen ? '收起' : '展開'}所有紀錄
                  </span>
                </button>

                {historyOpen && (
                  <div className="space-y-2">
                    {archives.map(a => (
                      <div key={a.id} className="relative">
                        <ArchiveCard archive={a} onDelete={deleteArchive} />
                      </div>
                    ))}
                  </div>
                )}

                {!historyOpen && (
                  <div className="space-y-2">
                    {archives.slice(0, 1).map(a => (
                      <div key={a.id}>
                        <ArchiveCard archive={a} onDelete={deleteArchive} />
                      </div>
                    ))}
                    {archives.length > 1 && (
                      <button
                        onClick={() => setHistoryOpen(true)}
                        className="w-full py-2 rounded-xl text-[10px] cursor-pointer whitespace-nowrap"
                        style={{ color: '#2a3a5a', border: '1px dashed #1a1a2e', background: '#07080e', fontFamily: FONT_BODY }}>
                        查看其餘 {archives.length - 1} 筆紀錄
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

      </div>

      <style>{`
        @keyframes panelShim { from { transform: translateX(-100%); } to { transform: translateX(100%); } }
        @keyframes panelDot { 0%,100% { opacity:.3;transform:scale(1); } 50% { opacity:1;transform:scale(1.5); } }
      `}</style>
    </div>
  );
}
